import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './Table.css';
import type { TableProps } from '@frontend/Models/Table/TableModels';
import Pagination from '../Pagination/Pagination';

/** Shared density class for table wrappers; use with pageSize for consistent layout. */
export function getDensityClass(size: number): string {
  if (size <= 7) return 'table-density-normal';
  if (size <= 20) return 'table-density-compact';
  if (size <= 50) return 'table-density-very-compact';
  return 'table-density-ultra-compact';
}

interface ExtendedTableProps<T> extends Omit<TableProps<T>, 'data' | 'rowKey'> {
  data: T[] | null;
  rowKey?: keyof T | string;
}

function Table<T extends Record<string, any>>({ 
  columns, 
  data, 
  emptyMessage = "No data found",
  emptyAction,
  rowKey = "id", // default key for React list rendering
  pagination
}: ExtendedTableProps<T>): JSX.Element | null {
  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return (
      <>
        <h3 className="no-data">{emptyMessage}</h3>
        {emptyAction && (
          <div className="center-container">
            {emptyAction}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={`${row[rowKey as string] ?? index}-${index}`}>
              {columns.map((column, colIndex) => (
                <td key={colIndex}>
                  {column.render ? (
                    column.render(row)
                  ) : (
                    <div className="d-flex">
                      {column.icon && (
                        <FontAwesomeIcon 
                          icon={column.icon} 
                          className="tableIcon" 
                        />
                      )}
                      <span>{column.accessor ? row[column.accessor] : null}</span>
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <Pagination
          currPage={pagination.currPage}
          onPageChange={pagination.onPageChange}
          pageLimit={pagination.pageLimit}
          pageSize={pagination.pageSize}
          onPageSizeChange={pagination.onPageSizeChange}
          totalItems={pagination.totalItems}
          itemLabel={pagination.itemLabel}
        />
      )}
    </div>
  );
}

export default Table;

